/**
 * Per-photo and set-level EXIF metadata validators for photogrammetry quality.
 *
 * These checks flag camera settings that are known to degrade 3D reconstruction.
 * Thresholds are derived from:
 *  - Bentley ContextCapture Acquisition Best Practices
 *  - Skyfish Photogrammetry Best Practices v3.31
 *  - RMA Communication Tower Capture Guide V16.0
 */
import { FileMetadataInfo, PhotoExifData } from '../../../models/uploaded-image-metadata.model';
import { ValidationWarning, ValidationWarningType } from '../../../models/validation-warning.model';

/** Max ISO before noise significantly degrades tie-point detection (Skyfish guide: "NEVER exceed ISO 1600"). */
const ISO_THRESHOLD = 1600;

/** Max exposure time (seconds). Faster = less motion blur. Skyfish guide recommends 1/500; 1/250 is the safe floor. */
const EXPOSURE_TIME_THRESHOLD = 1 / 250;

/** Minimum resolution in pixels. Below ~3MP the photogrammetry engine lacks detail for reliable point matching. */
const MIN_MEGAPIXELS = 3_000_000;

/** Max acceptable focal length standard deviation (mm). A prime lens produces 0; zoom drift beyond 2mm hurts calibration. */
const FOCAL_LENGTH_STDDEV_THRESHOLD = 2;

// --- Per-photo checks -----------------------------------------------------------

/** Low resolution reduces the density of detectable tie-points, producing coarser meshes. */
const isLowRes = (e: PhotoExifData) =>
  !!(e.imageWidth && e.imageHeight && e.imageWidth * e.imageHeight < MIN_MEGAPIXELS);

/** High ISO amplifies sensor noise, creating false features that confuse point matching. */
const isHighIso = (e: PhotoExifData) => !!(e.iso && e.iso > ISO_THRESHOLD);

/** Slow shutter while the drone is moving causes motion blur, making tie-points undetectable. */
const isSlowShutter = (e: PhotoExifData) =>
  !!(e.exposureTime && e.exposureTime > EXPOSURE_TIME_THRESHOLD);

/** Non-zero exposure bias shifts brightness away from the camera's metered value,
 *  causing over/under-exposure that varies as the drone orbits the structure. Must be exactly 0. */
const hasExposureBias = (e: PhotoExifData) => e.exposureBias != null && e.exposureBias !== 0;

const collectByCheck = (photos: FileMetadataInfo[], check: (e: PhotoExifData) => boolean) =>
  photos.filter((p) => p.exifData && check(p.exifData)).map((p) => p.name);

const addIfAny = (
  out: ValidationWarning[],
  files: string[],
  type: ValidationWarningType,
  message: string,
) => {
  if (files.length) out.push({ type, severity: 'warning', message, affectedFiles: files });
};

/** Checks individual photos for settings that hurt reconstruction quality. */
export const validateExifMetadata = (photos: FileMetadataInfo[]): ValidationWarning[] => {
  const w: ValidationWarning[] = [];
  addIfAny(
    w,
    collectByCheck(photos, isLowRes),
    ValidationWarningType.LOW_RESOLUTION,
    `Photo(s) have resolution below 3 megapixels. Low resolution reduces 3D model detail.`,
  );
  addIfAny(
    w,
    collectByCheck(photos, isHighIso),
    ValidationWarningType.HIGH_ISO,
    `Photo(s) have ISO above ${ISO_THRESHOLD}. High ISO introduces noise that degrades 3D reconstruction.`,
  );
  addIfAny(
    w,
    collectByCheck(photos, isSlowShutter),
    ValidationWarningType.SLOW_SHUTTER,
    `Photo(s) have shutter speed slower than 1/250s. This may cause motion blur.`,
  );
  addIfAny(
    w,
    collectByCheck(photos, hasExposureBias),
    ValidationWarningType.EXPOSURE_BIAS,
    `Photo(s) have non-zero exposure bias. Exposure bias should be 0 for photogrammetry.`,
  );
  return w;
};

// --- Set consistency checks -----------------------------------------------------

/**
 * Focal length variance across a set indicates zoom was used mid-flight.
 * The photogrammetry engine calibrates a single lens model per camera -- varying focal length
 * forces re-calibration per group, reducing alignment robustness.
 * Bentley guide: "Keep a constant focal length. Using a prime lens is recommended."
 */
const checkFocalLengthVariance = (fl: number[]): ValidationWarning | null => {
  if (fl.length <= 1) return null;
  const mean = fl.reduce((a, b) => a + b, 0) / fl.length;
  const stddev = Math.sqrt(fl.reduce((s, v) => s + (v - mean) ** 2, 0) / fl.length);
  if (stddev <= FOCAL_LENGTH_STDDEV_THRESHOLD) return null;
  return {
    type: ValidationWarningType.INCONSISTENT_FOCAL_LENGTH,
    severity: 'warning',
    message: `Focal length varies significantly (stddev: ${stddev.toFixed(
      1,
    )}mm). Use a constant focal length.`,
  };
};

const getCameraLabel = (e: PhotoExifData) =>
  `${e.cameraMake || ''} ${e.cameraModel || ''}`.trim() || null;

/**
 * Mixing cameras means different sensor sizes, lens distortion profiles, and color responses.
 * The engine must calibrate each camera independently, and cross-camera tie-point matching
 * is less reliable due to differing optical characteristics.
 */
const checkMixedCameras = (photos: FileMetadataInfo[]): ValidationWarning | null => {
  const cams = new Set(
    photos.map((p) => (p.exifData ? getCameraLabel(p.exifData) : null)).filter(Boolean),
  );
  if (cams.size <= 1) return null;
  return {
    type: ValidationWarningType.MIXED_CAMERAS,
    severity: 'warning',
    message: `Photos from ${cams.size} different cameras (${[...cams].join(
      ', ',
    )}). Mixing cameras can degrade alignment.`,
  };
};

/**
 * Different image dimensions usually indicate different cameras or cropped images.
 * Inconsistent dimensions break the assumption of uniform sensor geometry during calibration.
 */
const checkMixedDimensions = (photos: FileMetadataInfo[]): ValidationWarning | null => {
  const dims = new Set<string>();
  for (const { exifData: e } of photos) {
    if (e?.imageWidth && e?.imageHeight) dims.add(`${e.imageWidth}x${e.imageHeight}`);
  }
  if (dims.size <= 1) return null;
  return {
    type: ValidationWarningType.MIXED_DIMENSIONS,
    severity: 'warning',
    message: `Photos have ${dims.size} different resolutions (${[...dims].join(
      ', ',
    )}). Consistent dimensions recommended.`,
  };
};

/** Checks that the photo set uses consistent camera hardware and lens settings. */
export const validateSetConsistency = (photos: FileMetadataInfo[]): ValidationWarning[] => {
  const fl = photos
    .filter((p) => p.exifData?.focalLength != null)
    .map((p) => p.exifData?.focalLength as number);
  return [
    checkMixedCameras(photos),
    checkMixedDimensions(photos),
    checkFocalLengthVariance(fl),
  ].filter((w): w is ValidationWarning => w !== null);
};
